import { useState } from 'react'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import type { UserRoleName } from '../../../shared/api/users.api'

export interface AccountFormData {
  email: string
  password: string
  name: string
  role: UserRoleName
}

/**
 * Роли этапа 3 (с 23.09.2026). hint — что роли открыто: права выдаются по
 * кускам этапа (docs/stage-3-plan.md), подсказку обновлять вместе с правами.
 */
const BLOCK3_ROLES: Array<{ value: UserRoleName; label: string; hint?: string }> = [
  {
    value: 'ACCOUNTANT',
    label: 'Бухгалтер',
    hint: 'Бухгалтер: вводит курсы валют и факт продаж в «Планировании», видит коэффициенты наценки, публикует новости в ленте. Остальные разделы откроются позже.',
  },
  {
    value: 'LOGIST',
    label: 'Логист',
    hint: 'Логист: раздел «Нетарифка» — очередь проверки позиций из-за рубежа, запросы брокеру; публикует новости',
  },
  {
    value: 'WAREHOUSE_HEAD',
    label: 'Руководитель склада',
    hint: 'Руководитель склада: публикует новости в ленте компании. Остальные разделы откроются позже.',
  },
  { value: 'WAREHOUSE_QC', label: 'Приёмка и ОТК' },
  { value: 'WAREHOUSE_KEEPER', label: 'Кладовщик' },
]

function IconEye() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function IconEyeOff() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
}

export function AccountModal({
  initial,
  isEdit,
  onClose,
  onSave,
  saving,
  error,
}: {
  initial?: Partial<AccountFormData>
  isEdit: boolean
  onClose: () => void
  onSave: (data: AccountFormData) => void
  saving: boolean
  error: unknown
}) {
  const [email, setEmail] = useState(initial?.email ?? '')
  const [password, setPassword] = useState('')
  const [name, setName] = useState(initial?.name ?? '')
  const [role, setRole] = useState<AccountFormData['role']>(initial?.role ?? 'MANAGER')
  const block3Role = BLOCK3_ROLES.find((r) => r.value === role)
  const [showPassword, setShowPassword] = useState(false)

  const canSave = email.trim().length > 3 && (isEdit || password.length >= 6) && !saving

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">
        <h2 className="text-base font-semibold text-gray-900">
          {isEdit ? 'Редактировать аккаунт' : 'Создать новый аккаунт'}
        </h2>

        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Имя <span className="text-gray-400">(необязательно)</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Иван Петров"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            autoFocus={!isEdit}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@company.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Пароль{' '}
            {isEdit ? (
              <span className="text-gray-400">(оставьте пустым, чтобы не менять)</span>
            ) : (
              <span className="text-red-500">* (мин. 6 символов)</span>
            )}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isEdit ? 'Введите новый пароль' : 'Минимум 6 символов'}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            {password.length > 0 && (
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-600 transition-colors"
                title={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
              >
                {showPassword ? <IconEye /> : <IconEyeOff />}
              </button>
            )}
          </div>
          {isEdit && (
            <p className="text-xs text-gray-400 mt-1">
              Пароли хранятся в зашифрованном виде — просмотреть текущий невозможно. Введите новый
              пароль чтобы сменить его.
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Роль <span className="text-red-500">*</span>
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as typeof role)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="MANAGER">Менеджер по продажам</option>
            <option value="PROCUREMENT">Менеджер по закупкам</option>
            <option value="ROP">РОП · Руководитель отдела продаж</option>
            <option value="ROS">РОЗ · Руководитель отдела закупок</option>
            <option value="ASSISTANT">Помощник</option>
            <option value="GENERAL_DIRECTOR">Генеральный директор</option>
            <option value="ADMIN">Администратор</option>
            <optgroup label="Роли этапа 3">
              {BLOCK3_ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </optgroup>
          </select>
          {block3Role && (
            <p className="text-xs text-amber-600 mt-1">
              {block3Role.hint ??
                'Роль пока без доступа: сотрудник увидит только главную страницу с лентой новостей. Разделы откроются в следующих обновлениях.'}
            </p>
          )}
          {role === 'ASSISTANT' && (
            <p className="text-xs text-gray-400 mt-1">
              Менеджеров для помощника прикрепите после сохранения — в колонке «Город / Менеджеры»
              списка аккаунтов.
            </p>
          )}
          {role === 'GENERAL_DIRECTOR' && (
            <p className="text-xs text-gray-400 mt-1">
              Видит всё как администратор, но только в режиме просмотра (без изменений и удаления).
            </p>
          )}
        </div>

        {error != null && <ErrorMessage error={error} />}

        <div className="flex gap-3 pt-1">
          <button
            onClick={() => onSave({ email, password, name, role })}
            disabled={!canSave}
            className="px-5 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Сохранение...' : isEdit ? 'Сохранить' : 'Создать'}
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  )
}
